import React, { useDeferredValue, useState } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_Input } from "@danishagro/shared/src/components/atoms/Input/Input.component";
import { DA_Title } from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useBackgroundColor } from "../../../../hooks/useBackgroundColor/useBackgroundColor.hook";
import { usePadding } from "../../../../hooks/usePadding/usePadding.hook";
import { SSH_Departments } from "../../DepartmentsList";
import Map from "./components/Map/Map.component";
import { V1Props } from "./DepartmentMapV1.interface";
import { Department } from "./components/Department/Department.component";
import { DepartmentProps } from "./components/Department/Department.interface";
import S from "./DepartmentMapV1.module.scss";

const V1 = (props: V1Props) => {
    const { backgroundColorClass, hasBackgroundColor } = useBackgroundColor(props.backgroundColor);
    const { paddingClass } = usePadding(props.isCompact, hasBackgroundColor);
    const { getDictionaryString } = useTranslations();

    // Resolve center coordinates: prefer address coordinates, fallback to mapconfig
    const googleMapsCenter = props.googleMapsCenter;
    const resolvedCenterLat =
        props.centerLat ??
        ((!googleMapsCenter?.address?.coordinates?.isEmpty &&
            googleMapsCenter?.address?.coordinates?.lat) ||
            googleMapsCenter?.mapconfig?.centerCoordinates?.lat);
    const resolvedCenterLng =
        props.centerLng ??
        ((!googleMapsCenter?.address?.coordinates?.isEmpty &&
            googleMapsCenter?.address?.coordinates?.lng) ||
            googleMapsCenter?.mapconfig?.centerCoordinates?.lng);

    // Use department data from props instead of fetching
    const departmentsData = props.departments || [];

    // Search state
    const [_searchQuery, setSearchQuery] = useState<string>("");
    const searchQuery = useDeferredValue(_searchQuery);

    // Filter departments based on search query
    const filteredDepartments = departmentsData.filter((dept) => {
        const deptProps = dept.props || {};
        const deptName = deptProps.departmentName?.toLowerCase() || "";
        const address = deptProps.address?.toLowerCase() || "";
        const zipCode = deptProps.zipCode?.toLowerCase() || "";
        const city = deptProps.city?.toLowerCase() || "";
        const combined = `${deptName}_${address}_${zipCode}_${city}`;
        return searchQuery.length > 0 ? combined.includes(searchQuery.toLowerCase()) : true;
    });

    const [activeMarker, setActiveMarker] = useState<DepartmentProps | null>(null);

    /** Render content */
    const renderSearchContent = (
        <>
            <DA_Title h2>{getDictionaryString("Department.FindUs")}</DA_Title>
            <DA_Input
                icon={DA_IconNames.Zoom}
                placeholder={getDictionaryString("Department.SearchDepartments")}
                onChange={setSearchQuery}
                className={S.search}
            />
        </>
    );

    const getInnerHtml = () => {
        switch (props.type[0]) {
            case "LIST_AND_MAP":
                return (
                    <div className={S.inner}>
                        <div className={S.departmentWrapper}>
                            {renderSearchContent}

                            {/** Departments LIST and MAP */}
                            {filteredDepartments.length > 0 ? (
                                <div className={S.listWrapper}>
                                    <div className={S.listCount}>
                                        {filteredDepartments.length}{" "}
                                        {getDictionaryString("Department.Departments")}
                                    </div>
                                    <div className={S.list}>
                                        {filteredDepartments.map((item, index) => {
                                            if (item.props.googleMap !== null) {
                                                return (
                                                    <Department
                                                        key={index}
                                                        {...item}
                                                        className={cn(
                                                            activeMarker === item &&
                                                                S.activeDepartment
                                                        )}
                                                        onDepartmentChange={() =>
                                                            setActiveMarker(item)
                                                        }
                                                        activeMarker={item}
                                                    />
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div>{getDictionaryString("Department.NoItemsFound")}</div>
                            )}
                        </div>

                        {/** Map */}
                        <Map
                            onMarkerChange={setActiveMarker}
                            activeMarker={activeMarker}
                            className={S.mapWrapper}
                            departments={filteredDepartments}
                            googleMapsZoom={props?.googleMapsZoom}
                            centerLat={resolvedCenterLat}
                            centerLng={resolvedCenterLng}
                        />
                    </div>
                );

            default:
                return (
                    <div className={S.departmentWrapper}>
                        {/** Departments LIST */}
                        <SSH_Departments departments={filteredDepartments} />
                    </div>
                );
        }
    };

    return (
        <div className={cn(S.wrapper, paddingClass, backgroundColorClass)}>
            <DA_Container noVerticalPadding>
                {/** BODY */}
                {getInnerHtml()}
            </DA_Container>
        </div>
    );
};

export default V1;
